import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * BaseModal - Unified Modal Component
 *
 * Royal Gems - Luxury Diamond Trading Platform
 * Use this for all modals across the application.
 *
 * Features:
 * - Gold accent theme with signature top bar
 * - Playfair Display typography for titles
 * - Size variants: sm, md, lg, xl
 * - Optional subtitle and footer
 * - Smooth entry/exit animations
 */
import { useEffect, useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import { Overlay, ModalContainer, GoldBar, ModalHeader, HeaderContent, ModalTitle, ModalSubtitle, CloseButton, ModalBody, ModalFooter, } from './BaseModal.styles';
// Re-export styles for use in child components
export * from './BaseModal.styles';
export const BaseModal = ({ isOpen, onClose, title, titleAccent, subtitle, children, footer, size = 'lg', maxWidth, noPadding = false, closeOnOverlayClick = false, closeOnEscape = true, }) => {
    // Handle escape key
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && closeOnEscape) {
            onClose();
        }
    }, [closeOnEscape, onClose]);
    // Add/remove escape key listener
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, handleKeyDown]);
    if (!isOpen)
        return null;
    const handleOverlayClick = (e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
            onClose();
        }
    };
    // Parse title to highlight accent word if provided
    const renderTitle = () => {
        if (titleAccent && title.includes(titleAccent)) {
            const parts = title.split(titleAccent);
            return (_jsxs(_Fragment, { children: [parts[0], _jsx("span", { children: titleAccent }), parts.slice(1).join(titleAccent)] }));
        }
        return title;
    };
    return (_jsx(Overlay, { onClick: handleOverlayClick, children: _jsxs(ModalContainer, { "$size": size, "$maxWidth": maxWidth, role: "dialog", "aria-modal": "true", "aria-labelledby": "modal-title", children: [_jsx(GoldBar, {}), _jsxs(ModalHeader, { children: [_jsxs(HeaderContent, { children: [_jsx(ModalTitle, { id: "modal-title", children: renderTitle() }), subtitle && _jsx(ModalSubtitle, { children: subtitle })] }), _jsx(CloseButton, { onClick: onClose, "aria-label": "Close modal", children: _jsx(FaTimes, {}) })] }), _jsx(ModalBody, { "$noPadding": noPadding, children: children }), footer && (_jsx(ModalFooter, { children: footer }))] }) }));
};
export default BaseModal;
